import { useMemo } from "react";
import { useHttp } from "../http/useHttp";
import { useAxiosQuery, UseAxiosQueryOptions } from "../http/useAxiosQuery";
import { useDefaultError } from "@synota-io/synota-shared-ui";
import { paths } from "../generated-types";

type AdhocInvoicesOperation = paths["/dashboard/contracts/{contract_shared_uuid}/ad-hocs"]["post"];
type UseAdhocInvoicesResponse =
  AdhocInvoicesOperation["responses"][200]["content"]["application/json"];
type UseAdhocInvoicesRequestBody =
  AdhocInvoicesOperation["requestBody"]["content"]["application/json"];
type AdhocInvoicesPath = AdhocInvoicesOperation["parameters"]["path"];

export type IAdhocInvoice = UseAdhocInvoicesResponse["items"][number];
export type UseAdhocInvoicesInput = UseAdhocInvoicesRequestBody & {
  contract_uuid: AdhocInvoicesPath["contract_shared_uuid"];
};

export const useAdhocInvoices = (
  input: UseAdhocInvoicesInput = { contract_uuid: "" },
  options: Omit<UseAxiosQueryOptions<UseAdhocInvoicesResponse>, "queryKey" | "queryFn"> = {},
) => {
  const { client } = useHttp();

  const { status, isPending, isFetching, isLoading, data, error, refetch } =
    useAxiosQuery<UseAdhocInvoicesResponse>({
      queryKey: ["contract-adhoc-invoices", input],
      queryFn: ({ signal }) =>
        client.post(`/contracts/${input.contract_uuid}/ad-hocs`, input, { signal }),
      enabled: options.enabled !== false && !!input.contract_uuid,
      ...options,
    });

  const [invoices, hasNextPage] = useMemo(() => {
    if (!data?.data) {
      return [[], false];
    }
    return [data.data.items, data.data.has_next_page];
  }, [data]);

  useDefaultError(error);

  return {
    invoices,
    hasNextPage,
    error,
    isLoading,
    isFetching,
    isPending,
    status,
    refetch,
  };
};

export type UseAdhocInvoicesReturn = ReturnType<typeof useAdhocInvoices>;
